// Turns a user-attached image (their own real screenshot) into a JPEG data URL,
// downscaled and quality-capped to stay under the API's screenshot size limit and
// Discord's attachment ceiling. This replaced an html2canvas auto-capture, which
// re-rendered the DOM and could not reproduce backdrop-filter (the blurred topbar)
// or many effects — so the "screenshot" never matched the real page. A real file
// the user picks is pixel-accurate.
//
// Contract: NEVER throws — a bad/oversized file returns null and the feedback
// still sends as text.

/** Longest-edge cap; keeps hi-res screenshots readable but in budget. */
const MAX_DIMENSION = 1600;
const JPEG_QUALITIES = [0.85, 0.7, 0.55, 0.4];

// Per-image cap so up to 10 screenshots fit in one request (and under Discord's
// attachment ceiling). An image that still won't fit at the lowest quality is
// rejected (returns null) rather than sent. ~730KB of base64.
const MAX_DATA_URL_CHARS = 1_000_000;

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (event) => {
      URL.revokeObjectURL(url);
      reject(event);
    };
    img.src = url;
  });
}

/**
 * Read an image File into a downscaled JPEG data URL, or null when it is not an
 * image, fails to decode, or can't be squeezed under the size cap.
 */
export async function imageFileToDataUrl(file: File): Promise<string | null> {
  if (typeof document === "undefined") return null;
  if (!file.type.startsWith("image/")) return null;
  try {
    const img = await loadImage(file);
    const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
    const width = Math.max(1, Math.round(img.width * scale));
    const height = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    // Flatten onto the app background so a transparent PNG doesn't turn black.
    ctx.fillStyle = "#0d0c0b";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    for (const quality of JPEG_QUALITIES) {
      const dataUrl = canvas.toDataURL("image/jpeg", quality);
      if (dataUrl.length <= MAX_DATA_URL_CHARS) return dataUrl;
    }
    return null;
  } catch {
    return null;
  }
}
